import React, {
  useMemo,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext,
} from "react";
import { GetApp as GetAppIcon } from "@material-ui/icons";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useInfiniteScroll } from "./useInfiniteScroll";
import VisitColumn from "./visit.column";
import { ActivitiesColumn, AssessmentQuantityColumn } from "./fixed.columns";
import {
  BATCH_SIZE,
  DATA_TABLE_NAME,
  SOE_TABLE_MAPPING_PROPERTIES,
} from "../constants";
import { Accordion } from "../../../../app/components";
import { useScheduleEvents } from "../../../../app/contexts/schedule.events.context";
import { useUser } from "../../../../app/contexts/user.context";
import { isEmpty } from "../../utils/helpers";
import { download_simple_soe } from "../excel.exports/single.soe/excel.exports.single.soe";
import UploadSoeTable from "../excel.uploads/upload.soe.table";
import CustomTooltip from "../../../../app/components/tooltip/custom.tooltip";

const ENABLE_VIRTUAL_SCROLLING = false;

// Context to avoid prop drilling
const SOETableContext = createContext(null);

export const useSOETableContext = () => {
  const context = useContext(SOETableContext);
  if (!context) {
    throw new Error("useSOETableContext must be used within SOETableContext.Provider");
  }
  return context;
};

// Memoized header component
const TableHeader = React.memo(({ visitToUse }) => {
  return (
    <HeaderRow>
      {/* Activities header */}
      <ActivitiesHeaderCell>
        <CustomTooltip title="Activities" placement="top">
          <span>Activities</span>
        </CustomTooltip>
      </ActivitiesHeaderCell>
      
      {/* Assessment Quantity header */}
      <AssessmentQuantityHeaderCell>
        <CustomTooltip title="Assessment Quantity" placement="top">
          <span>Assessment Quantity</span>
        </CustomTooltip>
      </AssessmentQuantityHeaderCell>
      
      {/* Visit headers */}
      {visitToUse?.map((visit, i) => {
        const visitTooltipTitle = (
          <div style={{ textAlign: "center" }}>
            <div>{visit?.visitCategory || "No-Cat"}</div>
            <div>{visit?.visitName}</div>
            <div>{`${visit?.visitIntervalType || "Day"} ${visit?.visitStudyDay}`}</div>
            {visit?.visitVarianceType && (
              <div>{`+ ${visit?.visitVariance}`}</div>
            )}
          </div>
        );

        return (
          <CustomTooltip key={visit?.scheduleOfEventsVisitId || i} title={visitTooltipTitle} placement="top">
            <VisitHeaderCell>
              <TextDisplay>{visit?.visitCategory || "No-Cat"}</TextDisplay>
              <TextDisplay style={{ border: "none", fontSize: "1rem" }}>
                {visit?.visitName}
              </TextDisplay>
              <TextDisplay>
                {`${visit?.visitIntervalType || "Day"} ${visit?.visitStudyDay}`}
              </TextDisplay>
              {visit?.visitVarianceType && (
                <TextDisplay>
                  {`${visit?.visitVarianceType || "Day"} ${visit?.visitVariance}`}
                </TextDisplay>
              )}
            </VisitHeaderCell>
          </CustomTooltip>
        );
      })}
    </HeaderRow>
  );
});

TableHeader.displayName = "TableHeader";

// Memoized content component with context consumption
const TableContent = React.memo(({ visibleVisits }) => {
  const {
    visitToUse,
    assessmentToUse,
    visitsCount,
    mappingTotals,
    visitsAndAssessmentsMapping,
    visitCheckBoxStates,
    allCheckedStates,
    getCheckedSum,
    handleMappingChange,
    debounceOnHandleChange,
    handleAllCheck,
    fieldType,
    activitiesPercentile,
    readOnly,
  } = useSOETableContext();
  return (
    <ContentRow data-scroll-container>
      {/* Fixed Activities column */}
      <ActivitiesColumn
        assessments={assessmentToUse}
        totals={mappingTotals}
        activitiesPercentile={activitiesPercentile}
        visitsAndAssessmentsMapping={visitsAndAssessmentsMapping}
        handleChange={handleMappingChange}
        handleAllCheck={handleAllCheck}
        allCheckedStates={allCheckedStates}
        readOnly={readOnly}
      />
      
      {/* Assessment Quantity column */}
      <AssessmentQuantityColumn
        assessments={assessmentToUse}
        totals={mappingTotals}
        getCheckedSum={getCheckedSum}
        visitsAndAssessmentsMapping={visitsAndAssessmentsMapping}
      />
      
      {/* Visit columns */}
      {visitToUse?.slice(0, visibleVisits).map((visit, i) => (
        <VisitColumn
          key={visit?.scheduleOfEventsVisitId || i}
          visit={visit}
          visitIndex={i}
          assessments={assessmentToUse}
          totals={mappingTotals}
          visitsAndAssessmentsMapping={visitsAndAssessmentsMapping}
          visitCheckBoxStates={visitCheckBoxStates}
          handleChange={handleMappingChange}
          debounceOnHandleChange={debounceOnHandleChange}
          fieldType={fieldType}
          visitsCount={visitsCount}
          activitiesPercentile={activitiesPercentile}
          readOnly={readOnly}
        />
      ))}
    </ContentRow>
  );
});

TableContent.displayName = "TableContent";

/*
  This component is responsible for rendering the table of schedule of events
  with horizontal batch loading (infinite scroll) for columns.
  It uses the schedule events context to handle the schedule of events.
  It uses the study context to handle the study plan.
*/
export default function SOETable({
  fieldType,
  setHasChanges,
  updateContext,
  setSaveSOE,
  tableVisibility,
  setTableVisibility,
}) {
  const { id, scheduleId } = useParams();
  const {
    currentSOE,
    scheduleOfEventsVisits,
    scheduleOfEventsAssessments,
    visitsAndAssessmentsMapping,
    setVisitsAndAssessmentsMapping,
    setVisitsAndAssessmentsMappingChangeLog,
    originalSOEMapping,
    setTotalMappings,
  } = useScheduleEvents();
  const { isReadOnlyUser } = useUser();

  const accordionRef = useRef(null);
  const headerScrollRef = useRef(null);
  const contentScrollRef = useRef(null);

  const visitToUse = useMemo(
    () => scheduleOfEventsVisits.study || [],
    [scheduleOfEventsVisits],
  );

  const visitsCount = useMemo(() => visitToUse?.length || 0, [visitToUse]);

  // Use infinite scroll hook
  const {
    visibleItems: visibleVisits,
    isLoading,
    loadingMessage
  } = useInfiniteScroll({
    totalItems: visitsCount,
    batchSize: BATCH_SIZE,
    isVirtualScrollingEnabled: ENABLE_VIRTUAL_SCROLLING,
    containerSelector: '[data-scroll-container]'
  });

  const mappingTotals = useMemo(() => {
    const visits = visitToUse || [];
    
    // Early return for empty visits
    if (!visits.length) {
      return Array(6).fill([]);
    }

    // Create lookup map for total mappings - O(n) operation
    const totalMappingsMap = originalSOEMapping
      ?.filter((vasm) => vasm?.dataSet === "Total")
      ?.reduce((acc, vasm) => {
        if (vasm.visitName) {
          acc[vasm.visitName] = vasm;
        }
        return acc;
      }, {}) || {};

    // Filter and sort visits in a single chain for better performance
    const sortedVisits = visits
      .filter(visit => visit?.visitName)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Early return if no valid visits after filtering
    if (!sortedVisits.length) {
      return Array(6).fill([]);
    }

    // Transform sorted visits into totals matrix more efficiently
    const totals = SOE_TABLE_MAPPING_PROPERTIES.map(property => 
      sortedVisits.map(visit => {
        const vasm = totalMappingsMap[visit.visitName];
        return vasm?.[property] ?? null;
      })
    );

    return totals;
  }, [originalSOEMapping, visitToUse]);

  useEffect(() => {
    setTotalMappings(mappingTotals);
  }, [mappingTotals, setTotalMappings]);

  const assessmentToUse = useMemo(
    () => {
      const studyAssessments = scheduleOfEventsAssessments.study || [];
      return studyAssessments.filter(({ assessmentType }) =>
        ["Procedure", "Non Procedure"].includes(assessmentType),
      );
    },
    [scheduleOfEventsAssessments],
  );
  
  const visitCheckBoxStates = useMemo(() => {
    const states = {};
    
    (assessmentToUse || []).forEach((assessment) => {
      const assessmentKey = assessment.key || assessment.scheduleOfEventsAssessmentId;
      const assessmentMapping = visitsAndAssessmentsMapping[assessmentKey] || [];
      
      states[assessmentKey] = {};
      
      (visitToUse || []).forEach((visit) => {
        const visitKey = visit.scheduleOfEventsVisitId;
        const mapping = assessmentMapping.find(m => m.scheduleOfEventsVisitId === visitKey);
        states[assessmentKey][visitKey] = Boolean(mapping?.value);
      });
    });
    
    return states;
  }, [assessmentToUse, visitToUse, visitsAndAssessmentsMapping]);
  
  // Derive all-checked states from the mapping
  const allCheckedStates = useMemo(() => {
    const states = {};
    
    (assessmentToUse || []).forEach((assessment) => {
      const assessmentKey = assessment.key || assessment.scheduleOfEventsAssessmentId;
      const assessmentMapping = visitsAndAssessmentsMapping[assessmentKey] || [];
      
      const checkedMappings = assessmentMapping.filter(m => m.value);
      states[assessmentKey] = checkedMappings.length > 0 && 
        checkedMappings.length === (visitToUse || []).length;
    });
    
    return states;
  }, [assessmentToUse, visitToUse, visitsAndAssessmentsMapping]);

  const activitiesPercentile = 30;

  const getCheckedSum = useCallback((assessmentKey) => {
    const assessmentMapping = visitsAndAssessmentsMapping[assessmentKey] || [];
    
    // Use the mapping as the source of truth for both checked state and multiplier
    const sum = assessmentMapping.reduce((sum, mapping) => {
      // Ensure multiplier is treated as a number, default to 0 if empty or invalid
      const multiplier = mapping.multiplier;
      const multiplierValue = typeof multiplier === 'string' ? 
        (parseFloat(multiplier) || 0) : 
        (typeof multiplier === 'number' ? multiplier : 0);
      
      return sum + multiplierValue;
    }, 0);
    
    return sum;
  }, [visitsAndAssessmentsMapping]);

  const handleMappingChange = useCallback((
    assessmentKey,
    visitKey,
    value,
    key = "value",
  ) => {
    if (isReadOnlyUser) return;
    setHasChanges(true);
    
    // Convert multiplier values to numbers for proper calculation
    let processedValue = value;
    if (key === "multiplier") {
      processedValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    }
    
    setVisitsAndAssessmentsMapping((prevValue) => {
      const updatedState = { ...prevValue };

      if (updatedState[assessmentKey]) {
        const arrayIndex = updatedState[assessmentKey].findIndex(
          (item) => item.scheduleOfEventsVisitId === visitKey,
        );

        if (arrayIndex !== -1) {
          const updatedItem = {
            ...updatedState[assessmentKey][arrayIndex],
            [key]: processedValue,
          };
          
          // If editing multiplier with a non-empty value while unchecked, auto-check it
          if (key === "multiplier" || key === "uniqueNonUniqueForm") {
            if (value !== "" && !updatedItem.value) {
              updatedItem.value = true;
            } else if (updatedItem.value && (Number(value) === 0 || isEmpty(value))) {
              updatedItem.value = false;
            }

            if (key === "uniqueNonUniqueForm" && isEmpty(updatedItem.multiplier)) {
              updatedItem.multiplier = "1";
            }
          }
          
          updatedState[assessmentKey][arrayIndex] = updatedItem;
        }
      }
      return updatedState;
    });

    // Mark that there are mapping changes to save
    setVisitsAndAssessmentsMappingChangeLog({ someValue: "some change" });
  }, [
    setHasChanges,
    setVisitsAndAssessmentsMapping,
    setVisitsAndAssessmentsMappingChangeLog,
    isReadOnlyUser,
  ]);

  const handleAllCheck = useCallback((assessmentKey, isChecked) => {
    if (isReadOnlyUser) return;
    setHasChanges(true);
    
    setVisitsAndAssessmentsMapping((prevValue) => {
      const updatedState = { ...prevValue };

      if (updatedState[assessmentKey]) {
        updatedState[assessmentKey] = updatedState[assessmentKey].map(item => {
          const updatedItem = {
            ...item,
            value: isChecked,
          };
          
          if (isChecked) {
            // When checking: keep edited values, only restore empty ones
            if (!item.multiplier) {
              // Only restore if current multiplier is empty
              if (item.savedMultiplier) {
                updatedItem.multiplier = item.savedMultiplier;
              } else {
                updatedItem.multiplier = "1";
              }
            }
            // Keep uniqueNonUniqueForm or restore if empty
            if (!item.uniqueNonUniqueForm && item.savedUniqueNonUniqueForm) {
              updatedItem.uniqueNonUniqueForm = item.savedUniqueNonUniqueForm;
            }
            // Clear saved values since we're checked now
            updatedItem.savedMultiplier = undefined;
            updatedItem.savedUniqueNonUniqueForm = undefined;
          } else {
            // When unchecking: save current values and clear multiplier for sum = 0
            if (item.multiplier) {
              updatedItem.savedMultiplier = item.multiplier;
              updatedItem.multiplier = "";
            }
            if (item.uniqueNonUniqueForm) {
              updatedItem.savedUniqueNonUniqueForm = item.uniqueNonUniqueForm;
              updatedItem.uniqueNonUniqueForm = "";
            }
          }
          
          return updatedItem;
        });
      }
      
      return updatedState;
    });

    // Mark that there are mapping changes to save
    setVisitsAndAssessmentsMappingChangeLog({ someValue: "some change" });
  }, [setHasChanges, setVisitsAndAssessmentsMapping, setVisitsAndAssessmentsMappingChangeLog, isReadOnlyUser]);

  // Context value - memoized to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    visitToUse,
    assessmentToUse,
    visitsCount,
    mappingTotals,
    visitsAndAssessmentsMapping,
    visitCheckBoxStates,
    allCheckedStates,
    getCheckedSum,
    handleMappingChange,
    debounceOnHandleChange: (() => {
      const debounce = (func, wait) => {
        let timeoutId;
        return function (...args) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => func.apply(this, args), wait);
        };
      };
      return debounce((assessmentKey, visitKey, value, key = "value") => {
        if (isReadOnlyUser) return;
        handleMappingChange(assessmentKey, visitKey, value, key);
      }, 500);
    })(),
    handleAllCheck,
    fieldType,
    activitiesPercentile,
    readOnly: isReadOnlyUser,
  }), [
    visitToUse,
    assessmentToUse,
    visitsCount,
    mappingTotals,
    visitsAndAssessmentsMapping,
    visitCheckBoxStates,
    allCheckedStates,
    getCheckedSum,
    handleMappingChange,
    handleAllCheck,
    fieldType,
    isReadOnlyUser,
  ]);

  // Effect to handle updateContext from Controls component when in Study tab
  useEffect(() => {
    if (!updateContext) {
      setSaveSOE(false);
      return;
    }

    // Prevent save operations when user is readonly
    if (isReadOnlyUser) {
      setSaveSOE(false);
      return;
    }

    // When updateContext is true, trigger the save process
    setSaveSOE(true);
  }, [updateContext, setSaveSOE, isReadOnlyUser]);

  // Sync scroll between header and content
  useEffect(() => {
    const headerEl = headerScrollRef.current;
    const contentEl = contentScrollRef.current;
    
    if (!headerEl || !contentEl) return;
    
    const syncScroll = (source, target) => {
      return () => {
        target.scrollLeft = source.scrollLeft;
      };
    };
    
    const headerScrollHandler = syncScroll(headerEl, contentEl);
    const contentScrollHandler = syncScroll(contentEl, headerEl);
    
    headerEl.addEventListener('scroll', headerScrollHandler);
    contentEl.addEventListener('scroll', contentScrollHandler);
    
    return () => {
      headerEl.removeEventListener('scroll', headerScrollHandler);
      contentEl.removeEventListener('scroll', contentScrollHandler);
    };
  }, []);

  const handleExportSOE = () => {
    download_simple_soe({
      scheduleOfEventsVisits,
      scheduleOfEventsAssessments,
      visitsAndAssessmentsMapping,
      currentSOE,
    });
  };

  return (
    <SOETableContext.Provider value={contextValue}>
      <Accordion
        ref={accordionRef}
        scrollIntoView={true}
        width={"100%"}
        summary={"Schedule of Events"}
        defaultExpanded={tableVisibility.showVisitInformationTable}
        isLoading={isLoading}
        loadingMessage={loadingMessage}
        headerAction={
          <ButtonContainer>
            {!isReadOnlyUser && <UploadSoeTable
              studyId={id}
              scheduleId={scheduleId}
            />}
            <Tooltip title="Export SOE" arrow>
              <span>
                <StyledIconButton data-testid="export-soe-button" onClick={handleExportSOE}>
                  <GetAppIcon />
                </StyledIconButton>
              </span>
            </Tooltip>
          </ButtonContainer>
        }
        onClick={() => {
          setTableVisibility("showVisitInformationTable");
        }}
        data-table-name={DATA_TABLE_NAME.VISIT_INFORMATION_TABLE}
      >
        {tableVisibility.showVisitInformationTable && (
          <TableContainer data-table-name={DATA_TABLE_NAME.VISIT_INFORMATION_TABLE}>
            {/* Single scrollable container for both header and content */}
            <ScrollableWrapper 
              ref={contentScrollRef}
              data-table-container="true"
              onClick={(event) => event.stopPropagation()}
            >
              {/* Header with sticky top positioning */}
              <HeaderRow>
                <TableHeader visitToUse={visitToUse} />
              </HeaderRow>
              
              {/* Content - only passes visibleVisits, rest comes from context */}
              <TableContent visibleVisits={visibleVisits} />
            </ScrollableWrapper>
          </TableContainer>
        )}
      </Accordion>
    </SOETableContext.Provider>
  );
}

// Styled Components
const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--White);
  border: 1px solid var(--surface-300);
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

// const HeaderScrollable = styled.div`
//   max-width: calc(96vw);
//   width: 100%;
//   overflow-x: auto;
//   overflow-y: hidden;
//   background: var(--surface-100);
//   border-bottom: 2px solid var(--surface-400);
//   position: sticky;
//   top: 0;
//   z-index: 100;
  
//   scrollbar-width: none;
//   -ms-overflow-style: none;
  
//   &::-webkit-scrollbar {
//     display: none;
//   }
// `;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  width: fit-content;
  min-width: 100%;
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--surface-100);
  border-bottom: 2px solid var(--surface-400);
`;

const ScrollableWrapper = styled.div`
  max-width: calc(96vw);
  max-height: calc(100vh - 24em);
  width: 100%;
  overflow: auto;
  background: var(--White);
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--surface-100);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--surface-400);
    border-radius: 4px;
    
    &:hover {
      background: var(--surface-500);
    }
  }
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  width: fit-content;
  min-width: 100%;
`;

// Header cells with proper sticky positioning
const ActivitiesHeaderCell = styled.div`
  min-width: 520px;
  max-width: 520px;
  width: 520px;
  height: 120px;
  padding: 8px;
  font-weight: bold;
  font-size: 1rem;
  color: var(--text-primary, #1f2937);
  border-right: 1px solid var(--surface-300);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: var(--surface-100);
  position: sticky;
  left: 0;
  z-index: 30;
  box-sizing: border-box;
`;

const AssessmentQuantityHeaderCell = styled.div`
  min-width: 120px;
  max-width: 120px;
  width: 120px;
  height: 120px;
  padding: 8px;
  font-weight: bold;
  font-size: 1rem;
  color: var(--text-primary, #1f2937);
  border-right: 1px solid var(--surface-300);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: var(--surface-100);
  position: sticky;
  left: 520px;
  z-index: 30;
  box-sizing: border-box;
`;

const VisitHeaderCell = styled.div`
  min-width: 220px;
  max-width: 220px;
  width: 220px;
  height: 120px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background: var(--surface-100);
  border-right: 1px solid var(--surface-300);
  gap: 4px;
  overflow: hidden;
  box-sizing: border-box;
`;

const TextDisplay = styled.span`
  display: block;
  font-size: 0.875rem;
  padding: 4px 8px;
  border: 1px solid var(--surface-300);
  border-radius: 8px;
  color: var(--text-primary);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StyledIconButton = styled(IconButton)`
  color: var(--teal-700) !important;
  padding: 8px !important;
  
  &:hover {
    background-color: var(--teal-100) !important;
  }
  
  & .MuiSvgIcon-root {
    font-size: 1.5rem !important;
    width: 1.5rem !important;
    height: 1.5rem !important;
  }
`;
